import { useState, useEffect, useMemo } from "react";
import {
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Copy,
  Trash2,
  SquareDashedBottom,
  Box,
  Heading as HeadingIcon,
  Type,
  ImageIcon,
  MousePointerClick,
  FileText,
  PanelTop,
  PanelBottom,
  AppWindow,
  Grid,
  Tag,
  Minus,
  MoveVertical,
  Search,
  X,
  Plus,
  ChevronsUpDown,
} from "lucide-react";
import { useEditorStore } from "../store";
import { findNodeById } from "../tree-utils";
import type { SiteNode, ComponentType } from "@zirro/schema";

const NODE_ICONS: Record<string, typeof Box> = {
  Section: SquareDashedBottom,
  Container: Box,
  Card: AppWindow,
  FeatureGrid: Grid,
  Heading: HeadingIcon,
  Text: Type,
  Image: ImageIcon,
  Button: MousePointerClick,
  Badge: Tag,
  Divider: Minus,
  Spacer: MoveVertical,
  Header: PanelTop,
  Footer: PanelBottom,
};

function getNodeTextSnippet(node: SiteNode): string {
  const p = node.props ?? {};
  if (p.text && typeof p.text === "string") return p.text;
  if (p.label && typeof p.label === "string") return p.label;
  if (p.title && typeof p.title === "string") return p.title;
  if (p.heading && typeof p.heading === "string") return p.heading;
  if (p.logoText && typeof p.logoText === "string") return p.logoText;
  if (p.businessName && typeof p.businessName === "string") return p.businessName;
  if (p.alt && typeof p.alt === "string") return p.alt;
  if (Array.isArray(p.content) && p.content[0]?.text) return String(p.content[0].text);

  // Look into direct children for a representative heading or text
  if (node.children && node.children.length > 0) {
    const headingChild = node.children.find((c) => c.type === "Heading");
    if (headingChild) {
      const hText = getNodeTextSnippet(headingChild);
      if (hText) return hText;
    }
    const textChild = node.children.find((c) => c.type === "Text" || c.type === "Button");
    if (textChild) {
      const tText = getNodeTextSnippet(textChild);
      if (tText) return tText;
    }
  }

  // Fallback to readable role from ID if informative
  if (node.id.includes("hero")) return "Hero";
  if (node.id.includes("best") || node.id.includes("product")) return "Products";
  if (node.id.includes("about") || node.id.includes("story")) return "About";
  if (node.id.includes("contact")) return "Contact";
  if (node.id.includes("feat") || node.id.includes("val")) return "Features";
  if (node.id.includes("banner")) return "Banner";
  if (node.id.includes("cta")) return "Call to Action";

  return "";
}

export function LayersTab() {
  const document = useEditorStore((s) => s.document);
  const activePageId = useEditorStore((s) => s.activePageId);
  const selectedNodeId = useEditorStore((s) => s.selectedNodeId);
  const hoveredNodeId = useEditorStore((s) => s.hoveredNodeId);
  const selectNode = useEditorStore((s) => s.selectNode);
  const setHoveredNodeId = useEditorStore((s) => s.setHoveredNodeId);
  const removeNode = useEditorStore((s) => s.removeNode);
  const duplicateNode = useEditorStore((s) => s.duplicateNode);
  const moveNode = useEditorStore((s) => s.moveNode);
  const reorderNode = useEditorStore((s) => s.reorderNode);
  const addNode = useEditorStore((s) => s.addNode);

  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());
  const [dragOverNodeId, setDragOverNodeId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<"before" | "after" | "inside">("after");

  const activePage =
    document.pages.find((p) => p.id === activePageId) ?? document.pages[0];

  // Auto-expand parent path when selected node changes
  useEffect(() => {
    if (!selectedNodeId) return;
    const search = findNodeById(document, selectedNodeId);
    if (!search) return;

    // Un-collapse all ancestors
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      let curr = search.parent;
      while (curr && "id" in curr) {
        next.delete((curr as SiteNode).id);
        const parentSearch = findNodeById(document, (curr as SiteNode).id);
        curr = parentSearch?.parent ?? null;
      }
      return next;
    });
  }, [selectedNodeId, document]);

  const toggleCollapse = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const expandAll = () => setCollapsedNodes(new Set());

  const collapseAll = () => {
    const allContainers = new Set<string>();
    const collectContainers = (nodes: SiteNode[]) => {
      for (const n of nodes) {
        if (n.children && n.children.length > 0) {
          allContainers.add(n.id);
          collectContainers(n.children);
        }
      }
    };
    if (activePage) collectContainers(activePage.nodes);
    setCollapsedNodes(allContainers);
  };

  const handleDragStart = (e: React.DragEvent, nodeId: string) => {
    e.dataTransfer.setData("application/zirro-node-id", nodeId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, targetNode: SiteNode) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const isContainer = ["Section", "Container", "Card", "FeatureGrid"].includes(targetNode.type);

    if (isContainer && offsetY > rect.height * 0.25 && offsetY < rect.height * 0.75) {
      setDropPosition("inside");
    } else if (offsetY < rect.height / 2) {
      setDropPosition("before");
    } else {
      setDropPosition("after");
    }
    setDragOverNodeId(targetNode.id);
  };

  const handleDragLeave = () => {
    setDragOverNodeId(null);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverNodeId(null);

    const sourceId = e.dataTransfer.getData("application/zirro-node-id");
    const componentType = e.dataTransfer.getData("application/zirro-component") as ComponentType;

    if (sourceId && sourceId !== targetId) {
      reorderNode(sourceId, targetId, dropPosition);
    } else if (componentType) {
      addNode(dropPosition === "inside" ? targetId : null, componentType);
    }
  };

  const renderLayerNode = (node: SiteNode, depth = 0): React.ReactNode => {
    const isSelected = node.id === selectedNodeId;
    const isHovered = node.id === hoveredNodeId;
    const isCollapsed = collapsedNodes.has(node.id);
    const children = node.children ?? [];
    const hasChildren = children.length > 0;
    const isContainerType = ["Section", "Container", "Card", "FeatureGrid"].includes(node.type);
    const Icon = NODE_ICONS[node.type] ?? FileText;
    const snippet = getNodeTextSnippet(node);

    // Filter matching if search query present
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchesSelf =
        node.type.toLowerCase().includes(q) || snippet.toLowerCase().includes(q);
      const matchesChild = children.some((c) => getNodeTextSnippet(c).toLowerCase().includes(q) || c.type.toLowerCase().includes(q));
      if (!matchesSelf && !matchesChild) return null;
    }

    const isTarget = dragOverNodeId === node.id;
    let dropClass = "";
    if (isTarget) {
      if (dropPosition === "before") dropClass = "zirro-layer-item--drop-above";
      else if (dropPosition === "after") dropClass = "zirro-layer-item--drop-below";
      else if (dropPosition === "inside") dropClass = "zirro-layer-item--drop-inside";
    }

    return (
      <div key={node.id} className="zirro-layer-wrapper">
        <div
          className={`zirro-layer-item ${isSelected ? "selected" : ""} ${isHovered ? "hovered" : ""} ${dropClass}`}
          style={{ paddingLeft: `${depth * 10 + 6}px` }}
          draggable
          onDragStart={(e) => handleDragStart(e, node.id)}
          onDragOver={(e) => handleDragOver(e, node)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, node.id)}
          onClick={() => selectNode(node.id)}
          onMouseEnter={() => setHoveredNodeId(node.id)}
          onMouseLeave={() => setHoveredNodeId(null)}
        >
          {/* Chevron expand/collapse toggle */}
          {hasChildren || isContainerType ? (
            <button
              type="button"
              className="zirro-layer-toggle"
              onClick={(e) => toggleCollapse(node.id, e)}
              aria-label={isCollapsed ? "Expand node" : "Collapse node"}
            >
              {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
            </button>
          ) : (
            <span className="zirro-layer-toggle-spacer" />
          )}

          {/* Node Type Icon */}
          <span className="zirro-layer-icon">
            <Icon size={13} />
          </span>

          {/* Title & Snippet */}
          <div className="zirro-layer-info">
            <span className="zirro-layer-type">{node.type}</span>
            {snippet ? <span className="zirro-layer-snippet">"{snippet}"</span> : null}
          </div>

          {/* Children count badge */}
          {hasChildren ? (
            <span className="zirro-layer-count-badge">{children.length}</span>
          ) : null}

          {/* Actions on hover */}
          <div className="zirro-layer-actions">
            {isContainerType ? (
              <button
                type="button"
                className="zirro-btn-mini"
                onClick={(e) => {
                  e.stopPropagation();
                  addNode(node.id, "Text");
                }}
                title="Add element inside"
              >
                <Plus size={11} />
              </button>
            ) : null}
            <button
              type="button"
              className="zirro-btn-mini"
              onClick={(e) => {
                e.stopPropagation();
                moveNode(node.id, "up");
              }}
              title="Move Up"
            >
              <ChevronUp size={11} />
            </button>
            <button
              type="button"
              className="zirro-btn-mini"
              onClick={(e) => {
                e.stopPropagation();
                moveNode(node.id, "down");
              }}
              title="Move Down"
            >
              <ChevronDown size={11} />
            </button>
            <button
              type="button"
              className="zirro-btn-mini"
              onClick={(e) => {
                e.stopPropagation();
                duplicateNode(node.id);
              }}
              title="Duplicate"
            >
              <Copy size={11} />
            </button>
            <button
              type="button"
              className="zirro-btn-mini zirro-btn-danger"
              onClick={(e) => {
                e.stopPropagation();
                removeNode(node.id);
              }}
              title="Delete"
            >
              <Trash2 size={11} />
            </button>
          </div>
        </div>

        {/* Render Children if expanded */}
        {hasChildren && !isCollapsed ? (
          <div className="zirro-layer-children">
            {children.map((child) => renderLayerNode(child, depth + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="zirro-tab-content zirro-layers-tab">
      {/* Header with expand/collapse toggles */}
      <div className="zirro-tab-header">
        <h3>Layers</h3>
        <div style={{ display: "flex", gap: "4px" }}>
          <button
            type="button"
            className="zirro-btn-text"
            onClick={expandAll}
            title="Expand All Containers"
            style={{ fontSize: "11px", padding: "2px 6px" }}
          >
            Expand
          </button>
          <button
            type="button"
            className="zirro-btn-text"
            onClick={collapseAll}
            title="Collapse All Containers"
            style={{ fontSize: "11px", padding: "2px 6px" }}
          >
            Collapse
          </button>
        </div>
      </div>

      {/* Search Input Filter */}
      <div className="zirro-layer-search-box">
        <Search size={13} className="zirro-layer-search-icon" />
        <input
          type="text"
          placeholder="Filter layers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery ? (
          <button
            type="button"
            className="zirro-layer-search-clear"
            onClick={() => setSearchQuery("")}
          >
            <X size={12} />
          </button>
        ) : null}
      </div>

      {/* Tree container */}
      <div className="zirro-layer-tree">
        {document.header ? (
          <div className="zirro-layer-section">
            <div className="zirro-layer-section-title">Header</div>
            {renderLayerNode(document.header, 0)}
          </div>
        ) : null}

        <div className="zirro-layer-section">
          <div className="zirro-layer-section-title">
            Page ({activePage?.title ?? "Home"})
          </div>
          {activePage?.nodes.map((node) => renderLayerNode(node, 0))}
        </div>

        {document.footer ? (
          <div className="zirro-layer-section">
            <div className="zirro-layer-section-title">Footer</div>
            {renderLayerNode(document.footer, 0)}
          </div>
        ) : null}
      </div>
    </div>
  );
}
